import React from "react";
import { REPORT_HEADER_SHELL } from "../../../helpers/reportHeader";
import {
  buildInvoicePartiesLayout,
  resolveInvoiceLegalNamePresentation,
} from "../../../helpers/invoiceReportLayout";
import { ReportRule } from "../../report/ReportRule";
import { useOrganization } from "../../../context/OrganizationContext";
import { resolveOrgBranding } from "../../../helpers/resolveOrgBranding";
import "./InvoiceReportLayout.css";

function InvoiceCompanyBrand({ branding }) {
  const legal = resolveInvoiceLegalNamePresentation(
    branding.brandName,
    branding.legalNameLine,
  );
  const contactLines = branding.contactLines || [];

  return (
    <div className="invoice-company-brand">
      <div className="invoice-company-brand-left">
        {branding.logoUrl ? (
          <img
            src={branding.logoUrl}
            alt=""
            className="invoice-company-logo"
          />
        ) : null}
        <div className="invoice-company-identity">
          <div className="invoice-company-name">{branding.brandName}</div>
          {legal ? (
            <div
              className={
                legal.emphasis === "subtle"
                  ? "invoice-company-legal invoice-company-legal--subtle"
                  : "invoice-company-legal"
              }
            >
              {legal.text}
            </div>
          ) : null}
          {(branding.registrationLines || []).map((line) => (
            <div key={line} className="invoice-company-registration">
              {line}
            </div>
          ))}
        </div>
      </div>

      {contactLines.length > 0 ? (
        <div className="invoice-company-brand-right">
          {contactLines.map((line) => (
            <div key={line} className="invoice-company-contact-line">
              {line}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function BillToBlock({ billTo }) {
  if (!billTo?.name && !(billTo?.lines?.length > 0)) return null;

  return (
    <div className="invoice-party-col">
      <h2 className="invoice-party-heading">{billTo.title}</h2>
      {billTo.name ? (
        <div className="invoice-party-name">{billTo.name}</div>
      ) : null}
      {billTo.lines.map((line) => (
        <div key={line} className="invoice-party-line">
          {line}
        </div>
      ))}
    </div>
  );
}

function InvoiceDetailsBlock({ details }) {
  if (!(details?.rows?.length > 0)) return null;

  return (
    <div className="invoice-party-col invoice-party-col--details">
      <h2 className="invoice-party-heading">{details.title}</h2>
      {details.rows.map((row) => (
        <div key={row.label} className="invoice-detail-row">
          <span className="invoice-detail-label">{row.label}</span>
          <span className="invoice-detail-value">{row.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Invoice header: two-column company brand, then document title, then parties.
 */
export function InvoiceReportHeader({
  fields = [],
  docTitle = REPORT_HEADER_SHELL.invoice.docTitle,
  variant = "preview",
}) {
  const { settings } = useOrganization();
  const branding = resolveOrgBranding(settings);
  const parties = buildInvoicePartiesLayout(fields);
  const hasParties =
    Boolean(parties.billTo.name) ||
    parties.billTo.lines.length > 0 ||
    parties.details.rows.length > 0;
  const ruleVariant = variant === "print" ? "print" : "preview";

  return (
    <header className="invoice-report-header">
      <InvoiceCompanyBrand branding={branding} />

      <h1 className="invoice-doc-title">{docTitle}</h1>

      <ReportRule variant={ruleVariant} />

      {hasParties ? (
        <div className="invoice-parties-grid">
          <BillToBlock billTo={parties.billTo} />
          <InvoiceDetailsBlock details={parties.details} />
        </div>
      ) : null}
    </header>
  );
}
